import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { CreditCardSetupInitiatorDialog } from './CreditCardSetupInitiatorDialog';
import { useDisclosure } from '@withjoy/joykit';
import { useManagedSearchParam } from '@shared/hooks/useManagedSearchParam';
import {
  EventUserStatus,
  RegistryPayoutAccountWithdrawalStatus,
  ShippingAddressFragment,
  useCreateRegistryPayoutAccountMutation,
  useGetPayoutAccountInfoLazyQuery,
} from '@graphql/generated';
import { useAuth } from '@shared/components/AuthProvider';
import { getLocalStorage } from '@shared/core';
import { PayoutAccountInfo, Provider } from './CreditCardSetUp.context';
import { withWindow } from '@shared/utils/withWindow';
import { PayoutDialog } from './PayoutDialog/PayoutDialog';
import { usePayoutDialogController } from './PayoutDialog/PayoutDialog.controller';
import { isAddressValid, isUSAddress } from '@apps/registry/common/utils/shippingAddressValidations';
import { usePreviousValue } from '@shared/utils/hooks/usePreviousValue';

const CREDIT_CARD_SETUP_STARTED = 'credit:card:setup';
export const REFUND_IN_PROGRESS = 'credit:card:setup:refund';

// we're playing with this until we figure out the right amount of attempts and time.
const MAX_ATTEMPTS = 4;

export interface CreditCardSetupProviderProps extends Readonly<{}> {
  children: React.ReactNode;
  eventId?: string;
  registryShippingAddress: ShippingAddressFragment | null;
  handleInvalidShippingAddress?: () => void;
}

const CreditCardSetupProvider: React.FC<CreditCardSetupProviderProps> = ({ children, eventId, registryShippingAddress, handleInvalidShippingAddress }) => {
  const {
    currentUser: { activeEventUser },
  } = useAuth();
  const [payoutAccountInfo, setPayoutAccountInfo] = useState<PayoutAccountInfo | null>(null);
  const localStorage = getLocalStorage();
  const [attempts, setAttempts] = useState(0);
  const maxAttemptsReached = attempts >= MAX_ATTEMPTS;
  const isValidUSAddress = useMemo(() => isAddressValid(registryShippingAddress) && isUSAddress(registryShippingAddress), [registryShippingAddress]);
  const [getPayoutInfo, { loading: fetchingPayoutInfo }] = useGetPayoutAccountInfoLazyQuery({
    batchMode: 'fast',
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const payoutAccount = data.getRegistryEventData?.payoutAccount;
      setPayoutAccountInfo((payoutAccount as PayoutAccountInfo) || null);
    },
  });
  const [createPayoutAccount, { loading: initPayoutAccountLoading }] = useCreateRegistryPayoutAccountMutation({
    onCompleted: data => {
      localStorage.setItem(CREDIT_CARD_SETUP_STARTED, 'true');
      window.location.href = data.createRegistryPayoutAccount.onboardingUrl as string;
    },
  });

  const initKYCOnboarding = useCallback(() => eventId && createPayoutAccount({ variables: { eventId } }), [eventId, createPayoutAccount]);
  const initKYCOnboardingFromRefund = useCallback(
    (returnRoute: string) => {
      if (eventId) {
        localStorage.setItem(REFUND_IN_PROGRESS, 'true');
        createPayoutAccount({ variables: { eventId, returnRoute } });
      }
    },
    [eventId, localStorage, createPayoutAccount],
  );
  const fetchPayoutInfo = useCallback(() => eventId && getPayoutInfo({ variables: { eventId } }), [eventId, getPayoutInfo]);
  const [setupCreditCard, setSetupCreditCard] = useManagedSearchParam('setupCreditCard');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { withdrawBalance, loadingPayoutRequest, payoutAccountAllowed, isOpenPayoutDialog, onOpenPayoutDialog, onClosePayoutDialog, payoutStatus, accountNumber } =
    usePayoutDialogController(fetchPayoutInfo, payoutAccountInfo, eventId as string);

  //const dialogRef = useOuterClick(onToggle, isOpen);
  // State will be handle by a query param, if this query param is present the modal will close and open.
  useEffect(() => {
    if (setupCreditCard) {
      onOpen();
    } else {
      onClose();
    }
  }, [onOpen, setupCreditCard, onClose]);

  useEffect(() => {
    if (localStorage.getItem(CREDIT_CARD_SETUP_STARTED)) {
      if (!localStorage.getItem(REFUND_IN_PROGRESS)) {
        setSetupCreditCard('true');
      }
      localStorage.setItem(CREDIT_CARD_SETUP_STARTED, '');
    }
  }, [localStorage, fetchingPayoutInfo, payoutAccountInfo, setSetupCreditCard]);

  // we don't clear out historical failures, so we only emphasize the last withdrawal
  const hasFailedWithdrawals = useMemo(() => payoutAccountInfo?.withdrawals.at(-1)?.status === RegistryPayoutAccountWithdrawalStatus.failed, [payoutAccountInfo]);
  const hasPendingWithdrawals = useMemo(
    () => payoutAccountInfo?.withdrawals.some(withdrawal => withdrawal.status === RegistryPayoutAccountWithdrawalStatus.pending) ?? false,
    [payoutAccountInfo],
  );

  const prevHasPendingWithdrawals = usePreviousValue(hasPendingWithdrawals);
  const [hasDoneWithdrawal, setHasDoneWithdrawal] = useState(false);
  useEffect(() => {
    // a withdrawal is pending, so clear done withdrawal
    if (hasPendingWithdrawals) {
      setHasDoneWithdrawal(false);
      return;
    }
    // previous withdrawal has been completed without error
    if (!hasPendingWithdrawals && prevHasPendingWithdrawals && !hasFailedWithdrawals) {
      setHasDoneWithdrawal(true);
      return;
    }
  }, [hasFailedWithdrawals, prevHasPendingWithdrawals, hasPendingWithdrawals, setHasDoneWithdrawal]);
  useEffect(() => {
    return withWindow(
      global => {
        let timeout: number;
        if (payoutAccountInfo && (payoutAccountInfo.status === 'pending' || hasPendingWithdrawals)) {
          if (attempts <= MAX_ATTEMPTS) {
            timeout = global.setTimeout(() => {
              setAttempts(prevValue => prevValue + 1);
              fetchPayoutInfo();
            }, 10_000);
          }
        }

        return () => {
          if (timeout) {
            global.clearTimeout(timeout);
          }
        };
      },
      () => {},
    );
  }, [payoutAccountInfo, fetchPayoutInfo, eventId, attempts, setAttempts, hasPendingWithdrawals]);

  useEffect(() => {
    fetchPayoutInfo();
  }, [fetchPayoutInfo, eventId]);
  return (
    <Provider
      value={{
        hasDoneWithdrawal,
        hasFailedWithdrawals,
        hasPendingWithdrawals,
        setSetupCreditCard,
        fetchingPayoutInfo,
        payoutAccountInfo,
        maxAttemptsReached,
        initKYCOnboarding,
        fetchPayoutInfo,
        onOpenPayoutDialog,
        initKYCOnboardingFromRefund,
        initPayoutAccountLoading,
      }}
    >
      {children}
      <CreditCardSetupInitiatorDialog
        payoutAccountInfo={payoutAccountInfo}
        isOwner={Boolean(activeEventUser?.status === EventUserStatus.owner)}
        onClose={() => setSetupCreditCard(null)}
        isOpen={isOpen}
        initKYCOnboarding={initKYCOnboarding}
        initPayoutAccountLoading={initPayoutAccountLoading}
        maxAttemptsReached={maxAttemptsReached}
        isValidUSAddress={isValidUSAddress}
        handleInvalidShippingAddress={handleInvalidShippingAddress}
      />
      {payoutAccountInfo && (
        <PayoutDialog
          isOpen={isOpenPayoutDialog}
          onClose={onClosePayoutDialog}
          isPayoutAllowed={payoutAccountAllowed}
          availableBalance={payoutAccountInfo.balance?.available}
          withdrawBalance={withdrawBalance}
          fetchPayoutInfo={fetchPayoutInfo}
          status={payoutStatus}
          loadingPayoutRequest={loadingPayoutRequest}
          accountNumber={accountNumber}
        />
      )}
    </Provider>
  );
};

export { CreditCardSetupProvider };
